/**
 * /activity — non-admin recent activity feed scoped to the caller.
 * Server endpoint /api/audit returns the user's own rows automatically when
 * called without admin scope; we just render them.
 */

import { useQuery } from '@tanstack/react-query';
import { type ApiError, api } from '../lib/api';
import { formatDate } from '../lib/format';
import { Badge, Banner } from '../components/brand';

interface AuditRow {
  id: number;
  userId: string | null;
  action: string;
  accountId: string | null;
  bucket: string | null;
  objectKey: string | null;
  status: 'ok' | 'denied' | 'error';
  detail: unknown;
  createdAt: number;
}

interface AuditPage {
  items: AuditRow[];
  cursor: string | null;
}

export function Activity() {
  const q = useQuery({
    queryKey: ['audit', 'self'],
    queryFn: () => api.get<AuditPage>('/api/audit?limit=100'),
  });

  const items = q.data?.items ?? [];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 className="h-page">Recent activity</h1>
        <p className="muted" style={{ marginTop: 6 }}>
          Your last 100 actions on this tenant.
        </p>
      </div>

      {q.error && (
        <div style={{ marginBottom: 16 }}>
          <Banner variant="danger" code={(q.error as ApiError).code ?? 'ERROR'}>
            {(q.error as Error).message}
          </Banner>
        </div>
      )}

      <div className="bm-card-flush">
        <table className="bm-table">
          <thead>
            <tr>
              <th style={{ width: 170 }}>Time</th>
              <th style={{ width: 200 }}>Action</th>
              <th style={{ width: 80 }}>Status</th>
              <th>Target</th>
            </tr>
          </thead>
          <tbody>
            {q.isLoading && (
              <tr>
                <td
                  colSpan={4}
                  style={{ padding: 24, textAlign: 'center', color: 'var(--bm-text-muted)' }}
                >
                  Loading…
                </td>
              </tr>
            )}
            {!q.isLoading && items.length === 0 && (
              <tr>
                <td
                  colSpan={4}
                  style={{ padding: 24, textAlign: 'center', color: 'var(--bm-text-muted)' }}
                >
                  No activity yet.
                </td>
              </tr>
            )}
            {items.map((row) => (
              <tr key={row.id}>
                <td className="mono tnum" style={{ fontSize: 12, color: 'var(--bm-text-muted)' }}>
                  {formatDate(row.createdAt)}
                </td>
                <td className="mono" style={{ fontSize: 12 }}>
                  {row.action}
                </td>
                <td>
                  <Badge variant={statusVariant(row.status)}>{row.status}</Badge>
                </td>
                <td className="mono" style={{ fontSize: 12, color: 'var(--bm-text-soft)' }}>
                  {renderTarget(row) || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function statusVariant(s: AuditRow['status']): 'success' | 'warn' | 'danger' {
  if (s === 'ok') return 'success';
  if (s === 'denied') return 'warn';
  return 'danger';
}

function renderTarget(row: AuditRow): string {
  const parts: string[] = [];
  if (row.accountId) parts.push(row.accountId);
  if (row.bucket) parts.push(row.bucket);
  if (row.objectKey) parts.push(row.objectKey);
  return parts.join(' / ');
}
